import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* 배경 이미지 */}
      <div className="absolute inset-0 w-full h-full">
        <img 
          src="https://public.readdy.ai/ai/img_res/78d9866188093c8f06e0ca368ff3824a.jpg" 
          alt="Industrial Power Systems" 
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-800/60 to-slate-900/70"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 md:pt-32 pb-8">
        {/* 좌측 상단 메인 콘텐츠 */}
        <div className="text-left mb-8 md:mb-16">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold text-white mb-4 md:mb-6 tracking-tight leading-tight">
            {t('hero.title')}
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-100 font-light leading-relaxed whitespace-pre-line">
            {t('hero.subtitle')}
          </p>
        </div>

        {/* 중앙 콘텐츠 */}
        <div className="text-center">
          {/* 안심 프로세스 배지 - 최상단 강조 */}
          <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-8 py-2 md:py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mb-6 md:mb-8 shadow-2xl animate-pulse">
            <i className="ri-shield-check-line text-white text-lg md:text-2xl"></i>
            <span className="text-white font-bold text-sm md:text-lg">{t('hero.trustBadge')}</span>
            <i className="ri-verified-badge-fill text-white text-base md:text-xl"></i>
          </div>

          {/* 협력 배지 */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-6 mb-4">
            <div className="flex items-center gap-2 md:gap-3 px-4 md:px-8 py-2 md:py-4 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl md:rounded-2xl border-2 border-white/30 shadow-xl">
              <i className="ri-cpu-line text-sky-400 text-lg md:text-2xl"></i>
              <span className="text-white text-sm md:text-lg font-bold">{t('hero.spaceTech')}</span>
              <span className="text-amber-400 text-sm md:text-lg font-bold">- {t('hero.spaceTechRole')}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-white/70 text-xl md:text-2xl font-bold">×</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3 px-4 md:px-8 py-2 md:py-4 bg-gradient-to-br from-sky-600 to-sky-700 rounded-xl md:rounded-2xl border-2 border-white/30 shadow-xl">
              <i className="ri-global-line text-white text-lg md:text-2xl"></i>
              <span className="text-white text-sm md:text-lg font-bold">{t('hero.sepisSolution')}</span>
              <span className="text-amber-400 text-sm md:text-lg font-bold">- {t('hero.sepisSolutionRole')}</span>
            </div>
          </div>

          {/* 협력 결과 강조 */}
          <div className="mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <i className="ri-arrow-down-line text-emerald-400 text-xl md:text-2xl"></i>
              <span className="text-emerald-400 text-xs md:text-sm font-semibold tracking-wider">{t('hero.collaborationResult')}</span>
              <i className="ri-arrow-down-line text-emerald-400 text-xl md:text-2xl"></i>
            </div>
          </div>

          <div className="inline-block px-6 md:px-10 py-3 md:py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl md:rounded-2xl mb-6 md:mb-10 shadow-2xl border-2 border-white/20">
            <span className="italic text-amber-300 font-bold text-base md:text-xl tracking-wide mr-2">Excellent</span>
            <span className="text-white font-bold text-base md:text-xl tracking-wide">{t('hero.industrialSolution')}</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 tracking-tight">
            공장부터 빌딩까지 <span className="text-emerald-400">SEPIS</span>
          </h2>

          <p className="text-base md:text-xl text-gray-100 mb-8 md:mb-12 font-medium px-4">
            {t('hero.mainSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-6 mb-10 md:mb-16 px-4">
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/products')}
              className="w-full sm:w-auto px-8 md:px-10 py-3 md:py-4 bg-white/10 backdrop-blur-md text-white border-2 border-white/30 rounded-lg text-base md:text-lg font-semibold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('hero.viewProducts')}
            </button>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/contact')}
              className="w-full sm:w-auto px-8 md:px-10 py-3 md:py-4 bg-white/10 backdrop-blur-md text-white border-2 border-white/30 rounded-lg text-base md:text-lg font-semibold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('hero.contactUs')}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 md:gap-8 max-w-4xl mx-auto px-2">
            <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
              <div className="text-2xl md:text-4xl font-bold text-sky-400 mb-1 md:mb-2">4~8%</div>
              <div className="text-xs md:text-sm text-gray-200">{t('hero.stats.savings')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
              <div className="text-2xl md:text-4xl font-bold text-sky-400 mb-1 md:mb-2">13~22{t('hero.stats.recovery') === '투자비 회수 기간' ? '개월' : ' mo'}</div>
              <div className="text-xs md:text-sm text-gray-200">{t('hero.stats.recovery')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
              <div className="text-2xl md:text-4xl font-bold text-sky-400 mb-1 md:mb-2">1,000+</div>
              <div className="text-xs md:text-sm text-gray-200">{t('hero.stats.cases')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-white text-2xl md:text-3xl"></i>
      </div>
    </section>
  );
}
